import { Link, useLocation } from "react-router-dom";
import { Mail, MapPin, Facebook, Instagram, Linkedin, Youtube, Twitter } from "lucide-react";
import { DOCM_LOGO } from "@/data/docm";
import { useLiveStats } from "@/hooks/useLiveStats";

const SOCIALS = [
  { key: "ig", label: "Instagram", icon: Instagram, href: "https://www.instagram.com/docm_declarationconsciouness/" },
  { key: "fb", label: "Facebook",  icon: Facebook,  href: "https://www.facebook.com/DeclarationOfConsciousness/" },
  { key: "in", label: "LinkedIn",  icon: Linkedin,  href: "https://www.linkedin.com/company/declaration-of-consciousness-movement/" },
  { key: "x",  label: "X (Twitter)", icon: Twitter, href: "https://x.com/ConsciousnessDC" },
  { key: "yt", label: "YouTube",   icon: Youtube,   href: "https://www.youtube.com/@declarationconsciouness" },
];

const FOOTER_COLS = [
  {
    title: "Movement",
    links: [
      { label: "Home", href: "/" },
      { label: "About DoCM", href: "/about" },
      { label: "9 Principles", href: "/about#principles" },
      { label: "Our Founder", href: "/our-founder" },
      { label: "Leadership", href: "/leadership" },
      { label: "Stories & Updates", href: "/stories" },
    ],
  },
  {
    title: "Act",
    links: [
      { label: "Sign the Declaration", href: "/one-conscious-act" },
      { label: "Random Acts of Consciousness", href: "/random-acts" },
      { label: "Consciousness Wall", href: "/random-acts#wall" },
      { label: "Community Events", href: "/community-acts" },
      { label: "11·11 Consciousness Day", href: "/1111" },
      { label: "Yoga Day · June 21", href: "/yoga-day" },
      { label: "Voice of Consciousness", href: "/voice-of-consciousness" },
      { label: "Conscious Super App", href: "/conscious-super-app" },
    ],
  },
  {
    title: "Lead",
    links: [
      { label: "Get Involved", href: "/get-involved" },
      // 2026-07-27 · Bug report — footer CTAs now point at the SAME create-flows
      // the "Join The Movement" navbar dropdown uses, so behavior is consistent:
      //   • Global Ambassador → Create Conscious Act (auto-promotes on submit)
      //   • Partner → Create Event (auto-promotes on submit)
      { label: "Be a Global Ambassador", href: "/random-acts?path=individual#rac-form" },
      { label: "Become a Partner", href: "/random-acts?path=community#rac-form" },
      { label: "Contribute", href: "/contribute" },
      { label: "Nominate a Hero", href: "/nominate-hero" },
    ],
  },
  {
    title: "Connect",
    links: [
      { label: "Contact us", href: "/contact" },
      { label: "My Dashboard", href: "/my-dashboard" },
      { label: "Newsletter", href: "/get-involved#stay-connected" },
      { label: "Movement Whitepaper", href: "/about#whitepaper" },
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Admin Sign-in", href: "/admin/voices" },
    ],
  },
  {
    title: "Discover",
    links: [
      { label: "DOCM Directory", href: "/directory" },
      { label: "Explore Organisations", href: "/directory" },
      { label: "List Your Organisation", href: "/directory/add" },
      { label: "My Directory", href: "/directory/dashboard" },
      { label: "DOCM Partners", href: "/partners" },
    ],
  },
];

export default function Footer() {
  const location = useLocation();
  const stats = useLiveStats();
  const countries = stats?.total_countries || 0;
  const tagline = countries > 1
    ? `A global movement · serving ${countries.toLocaleString()} ${countries === 1 ? "nation" : "nations"}`
    : "A global movement";

  const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  return (
    <footer
      data-testid="footer"
      className="relative bg-docm-ink text-docm-cream pt-20 pb-10 overflow-hidden"
    >
      <div className="pointer-events-none absolute -right-40 -top-40 w-[600px] h-[600px] rounded-full border border-docm-gold/10" />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-2 md:grid-cols-10 gap-10 md:gap-8">
          <div className="col-span-2 md:col-span-10">
            <Link to="/" data-testid="footer-logo">
              <div className="inline-flex items-center gap-3 rounded-2xl bg-docm-cream/95 px-4 py-2.5 border border-docm-cream/20 shadow-[0_8px_24px_-10px_rgba(0,0,0,0.5)]">
                <img src={DOCM_LOGO} alt="DOCM" className="h-10 md:h-11 w-auto" />
              </div>
            </Link>
            <p className="mt-6 font-display italic text-xl md:text-2xl lg:text-3xl text-docm-cream/90 max-w-xl leading-snug">
              &ldquo;We the People of Earth, Come Together United in Consciousness as One Humanity.&rdquo;
            </p>
            <div className="mt-7 space-y-3 text-sm text-docm-cream/70">
              <div className="flex items-center gap-3">
                <Mail size={14} className="text-docm-gold" />
                <a
                  href="mailto:info@signdc.org"
                  data-testid="footer-email"
                  className="hover:text-docm-cream transition-colors"
                >
                  info@signdc.org
                </a>
                <span className="text-docm-cream/30">·</span>
                <Link
                  to="/contact"
                  data-testid="footer-contact-link"
                  className="hover:text-docm-cream transition-colors underline decoration-docm-gold/40 decoration-1 underline-offset-4"
                >
                  Contact us
                </Link>
              </div>
              <div className="flex items-center gap-3">
                <MapPin size={14} className="text-docm-gold" />
                <span data-testid="footer-live-tagline">{tagline}</span>
              </div>
            </div>
          </div>

          {FOOTER_COLS.map((col) => (
            <div key={col.title} className="md:col-span-2">
              <div className="label-eyebrow text-docm-gold mb-4">{col.title}</div>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link
                      to={l.href}
                      onClick={(e) => {
                        // Special-case same-page hash anchors so the browser scrolls smoothly.
                        if (l.href.startsWith("/#") && location.pathname === "/") {
                          e.preventDefault();
                          const el = document.getElementById(l.href.slice(2));
                          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                        }
                      }}
                      data-testid={`footer-link-${slug(l.label)}`}
                      className="text-sm text-docm-cream/70 hover:text-docm-cream transition-colors text-left"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
              {col.title === "Connect" && (
                <div className="flex flex-wrap gap-2.5 mt-5">
                  {SOCIALS.map(({ key, label, icon: Icon, href }) => (
                    <a
                      key={key}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`social-${key}`}
                      aria-label={label}
                      title={label}
                      className="w-9 h-9 rounded-full border border-docm-gold/30 flex items-center justify-center text-docm-gold hover:bg-docm-gold hover:text-docm-ink transition-all duration-300 hover:-translate-y-0.5"
                    >
                      <Icon size={14} strokeWidth={2} />
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-docm-cream/10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="text-xs text-docm-cream/50">
            © {new Date().getFullYear()} Declaration of Consciousness Movement. All rights reserved.
            <span className="mx-2 text-docm-cream/30">·</span>
            <Link
              to="/privacy"
              data-testid="footer-privacy-link"
              className="text-docm-cream/60 hover:text-docm-gold underline underline-offset-2 transition-colors"
            >
              Privacy Policy
            </Link>
          </div>
          <div className="text-xs text-docm-cream/40 font-display italic">
            One Earth · One Humanity · One Shared Future · Random Acts of Consciousness
          </div>
        </div>
      </div>
    </footer>
  );
}
